<template>
  <form-layout v-if="isBatchLoading" is-loading title="Batch" @on-dismiss="dismiss" />
  <form-layout
    v-else-if="batch && canViewBatch(batch.ownerId)"
    :is-loading="false"
    :title="`Batch #${batch.invoiceNumber}`"
    bottom-bar-stick-bottom
    @on-dismiss="dismiss"
  >
    <div class="description">
      <vs-wrapper>
        <created-info
          :created-time="batch.created ?? undefined"
          :creator-sid="batch.createdBy ?? ''"
          text-type="subdued"
        />
        <vs-text v-if="batch.memo" type="paragraph">
          <v-icon>mdi-bookmark</v-icon>
          {{ batch.memo }}
        </vs-text>
        <vs-text type="paragraph">
          <v-icon>mdi-calendar-month</v-icon>
          {{ formattedBatchDate }}
        </vs-text>
        <vs-text
          v-if="batch.paymentTerms != null && batch.paymentTerms !== BatchPaymentTerms.Custom"
          type="paragraph"
        >
          <v-icon>mdi-receipt-clock</v-icon>
          {{ batchPaymentTermsString(batch.paymentTerms) }}
        </vs-text>
        <vs-text type="paragraph" v-if="batch.dueDate">
          <v-icon>mdi-calendar-clock</v-icon>
          {{ formattedDueDate }}
        </vs-text>
      </vs-wrapper>
    </div>

    <qbo-feature-promotion v-if="shouldShowQboPromo" />
    <div v-if="!loadingBatchSyncTargets" class="mt-8">
      <div v-for="target in batchSyncTargets" :key="`form-${target.id}`">
        <batch-form-submission
          v-if="target.customFieldsSchemaId !== null"
          :name="target.name"
          :app-id="target.id"
          :schema-version-id="target.customFieldsSchemaId"
          :custom-data="target.customData"
        />
      </div>
    </div>

    <div v-if="isRateViewer" class="mt-8">
      <vs-wrapper>
        <v-skeleton-loader v-if="loadingBatchSyncTargets" type="image" rounded />
        <template v-for="target in batchSyncTargets" v-else>
          <qbd-batch-sync-card
            v-if="target.type == 'qbd'"
            :invoice-id="batch.id"
            :target="target"
            @sync-requested="handleQbdSyncRequested"
          />
          <batch-sync-card
            v-else
            :batch="batch"
            :invoice-id="batch.id"
            :target="target"
            @show-history="showHistoryForTarget"
          />
        </template>
      </vs-wrapper>
    </div>

    <vs-wrapper>
      <v-row justify="center" class="mt-4 mb-8">
        <v-col v-if="canEditBatch(batch.ownerId)" class="shrink">
          <vs-button
            icon="mdi-pencil"
            type="secondary"
            class="btn mx-4"
            @click="toggleShowEditForm"
          />
          <vs-text class="btn-text">Edit</vs-text>
        </v-col>
        <v-col class="shrink">
          <vs-button
            icon="file_download"
            type="secondary"
            :loading="exportCsvSaving"
            class="btn mx-4"
            @click="exportBatchToCsv"
          />
          <vs-text class="btn-text">Download<br />CSV</vs-text>
        </v-col>
        <v-col class="shrink">
          <vs-button
            icon="mdi-file-pdf-box"
            type="secondary"
            :loading="exportPdfSaving"
            class="btn mx-4"
            @click="exportBatchToPdf"
          />
          <vs-text class="btn-text">Download<br />PDF</vs-text>
        </v-col>
      </v-row>
    </vs-wrapper>

    <v-divider />

    <div class="mt-8">
      <vs-wrapper>
        <vs-heading data-test="job-form-title" type="overline">Jobs</vs-heading>
        <div class="d-flex justify-space-between align-start">
          <batch-side-sheet-progress-bar :job-status-count="jobCountByStatus" class="flex-grow-1" />
          <batch-side-sheet-filter
            v-model="selectedFilters"
            :disabled="selectedJobs.length > 0"
            :filter-options="filterOptions"
          />
        </div>
        <vs-wrapper v-if="!isLoading">
          <job-card
            v-for="job in batchJobs"
            :key="job.id"
            :job="job"
            show-checkbox
            show-line-items
            :is-selected="isJobSelected(job)"
            :link-to="{
              name: `${route.name}-ticket-info-panel`,
              params: {
                id: `${job.number}`,
              },
            }"
            @selected="(selectedJob) => selectedJobs.push(selectedJob)"
            @deselected="
              (unselectedJob) => {
                selectedJobs = selectedJobs.filter((j) => j.id !== unselectedJob.id)
              }
            "
          />
        </vs-wrapper>
        <v-progress-circular v-else :size="64" :width="6" color="primary" indeterminate />
        <job-search-pagination
          :query="query"
          :current-page="getCurrentPage"
          :update-page="updatePage"
        />
      </vs-wrapper>
    </div>
    <template #action-bar>
      <div
        v-if="batch.totalAmount != null && isRateViewer"
        style="display: flex; justify-content: space-between; margin-top: 15px; margin-bottom: 15px"
      >
        <div>
          <vs-heading type="heading">Subtotal</vs-heading>
        </div>
        <div>
          <vs-heading type="title">{{ formattedBatchTotalAmount }}</vs-heading>
        </div>
      </div>
      <vs-wrapper v-if="selectedJobs.length" direction="horizontal">
        <vs-button
          v-if="bulkUpdateEnabled"
          :label="updateJobsLabel"
          full-width
          @click="isBulkUpdateFormOpen = true"
        />
        <vs-button
          v-if="showCheckbox"
          :label="selectAllButtonLabel"
          full-width
          type="secondary"
          @click="handleSelectDeselectAll"
        />
      </vs-wrapper>
    </template>

    <template #snack-bar-wrapper>
      <vs-snackbar :value="!!snackbarMessage" sticky type="info" @input="clearSnackbarMessage"
        >{{ snackbarMessage }}
      </vs-snackbar>
      <vs-snackbar :value="showSnackbarError" sticky type="error" @input="clearSnackbarError">
        {{ jobsError || exportCsvError || exportPdfError }}
      </vs-snackbar>
    </template>

    <right-drawer :is-visible="isBulkUpdateFormOpen">
      <bulk-update-form
        :allow-add-to-batch="false"
        :selected-jobs="selectedJobs"
        @dismiss="isBulkUpdateFormOpen = false"
        @updated="handleBulkUpdate"
        @bottom-sheet-closed="isBulkUpdateBottomSheetOpen = false"
        @bottom-sheet-open="isBulkUpdateBottomSheetOpen = true"
      ></bulk-update-form>
    </right-drawer>

    <template #bottom-sheet>
      <vs-bottom-sheet
        :is-visible="
          isJobFormVisible ||
          showEditForm ||
          showHistoryForTargetId !== null ||
          isQbdBottomSheetOpen
        "
        :full-height="isJobFormVisible || showHistoryForTargetId != null"
      >
        <router-view v-if="isJobFormVisible" @job-saved="handleJobSaved" />
        <batch-sync-details-edit-form
          v-else-if="isQbdBottomSheetOpen"
          :batch-id="batch.id"
          :sync-target-id="qbdSyncTargetId ?? ''"
          @batch-edit-clicked="
            () => {
              isQbdBottomSheetOpen = false
              toggleShowEditForm()
            }
          "
          @dismiss="isQbdBottomSheetOpen = false"
        />
        <batch-edit-form
          v-else-if="!isJobFormVisible && showEditForm"
          :id="props.id"
          @dismiss="toggleShowEditForm"
        />
        <batch-sync-history-panel
          v-else-if="showHistoryForTargetId != null"
          :batch-id="props.id"
          :target-id="showHistoryForTargetId"
          @dismiss="closeHistoryPanel"
        />
      </vs-bottom-sheet>
    </template>
  </form-layout>
</template>

<script setup lang="ts">
import FormLayout from '../common/FormLayout.vue'
import CreatedInfo from '../common/CreatedInfo.vue'
import VsWrapper from '../vision/VsWrapper.vue'
import { useStore } from 'vuex-composition-helpers'
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import {
  ACTIVE,
  ASSIGNED_TO_SERVICE_PROVIDER,
  CANCELLED,
  COMPLETED,
  PAUSED,
  REQUESTED,
  SCHEDULED,
} from '@/models/job'
import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router/composables'
import { usePagination } from '@/components/jobs/pagination'
import { useJobsQuery } from '@/components/jobs/query'
import { useJobsSearch } from '@/components/jobs/search'
import { useBatchViewFilters } from '@/components/jobs/filters'
import { useBulkUpdate } from '@/components/jobs/bulk-update'
import BulkUpdateForm from '@/components/jobs/BulkUpdateForm.vue'
import RightDrawer from '@/components/common/RightDrawer.vue'
import { useInvoiceQuery, useInvoiceSyncTargetsQuery } from '@/api/invoice'
import moment from 'moment'
import JobCard from '@/components/jobs/job-search-page/JobCard.vue'
import BatchSideSheetFilter from '@/components/batches/BatchSideSheetFilter.vue'
import BatchSideSheetProgressBar from '@/components/batches/BatchSideSheetProgressBar.vue'
import { BatchPaymentTerms, batchPaymentTermsString } from '@/models/batch'
import BatchEditForm from '@/components/batches/BatchEditForm.vue'
import BatchSyncCard from '@/components/batches/BatchSyncCard.vue'
import QbdBatchSyncCard from '@/components/batches/QuickBooksDesktop/BatchSyncCard.vue'
import BatchSyncHistoryPanel from '@/components/batches/BatchSyncHistoryPanel.vue'
import { useBatchCsvExport, useBatchPdfExport } from '@/components/batches/export'
import { useCurrencyFormatter } from '@/utils/number-formatting'
import VsButton from '@/components/vision/VsButton.vue'
import VsText from '@/components/vision/VsText.vue'
import JobSearchPagination from '@/components/jobs/job-search-page/JobSearchPagination.vue'
import VsSnackbar from '@/components/vision/VsSnackbar.vue'
import VsHeading from '@/components/vision/VsHeading.vue'
import VsBottomSheet from '@/components/vision/VsBottomSheet.vue'
import { JobSearchResponse } from '@/models/types/job-search-response'
import QboFeaturePromotion from '@/components/batches/Promotion/QboFeaturePromotion.vue'
import { usePromotionQuery } from '@/components/batches/Promotion/usePromotionQuery'
import BatchFormSubmission from '@/components/batches/BatchFormSubmission.vue'

const orderedStatuses = ['completed', 'active', 'unstarted']
const jobStatuses = [
  {
    id: REQUESTED,
    name: 'Requested',
  },
  {
    id: ASSIGNED_TO_SERVICE_PROVIDER,
    name: 'Assigned To Service Provider',
  },
  {
    id: SCHEDULED,
    name: 'Scheduled',
  },
  {
    id: ACTIVE,
    name: 'Active',
  },
  {
    id: PAUSED,
    name: 'Paused',
  },
  {
    id: COMPLETED,
    name: 'Completed',
  },
  {
    id: CANCELLED,
    name: 'Cancelled',
  },
]

const formatter = useCurrencyFormatter()

interface Props {
  id: string
  parentRouteName: string
  parentRouteParams?: Record<string, string>
}

const props = withDefaults(defineProps<Props>(), {
  parentRouteParams: () => ({}),
})

const store = useStore()
const route = useRoute()
const router = useRouter()

// Local Data/State
const snackbarMessage = ref<string | null>(null)
const showSnackbarError = ref<boolean>(false)
const selectedFilters = ref<{ id: string; name: string }[]>([])
const isBulkUpdateFormOpen = ref(false)
const isBulkUpdateBottomSheetOpen = ref(false)
const isQbdBottomSheetOpen = ref(false)
const qbdSyncTargetId = ref<string | null>(null)

const showEditForm = ref(false)
const showHistoryForTargetId = ref<string | null>(null)

const idRef = computed(() => props.id)
// Paginated Fetch Jobs API
const bulkUpdateEnabled = useBulkUpdate()
const filters = useBatchViewFilters(idRef, selectedFilters)
const { getCurrentPage, updatePage } = usePagination()
const itemsPerPage = ref(20)
const query = reactive(
  useJobsQuery(
    filters,
    getCurrentPage,
    itemsPerPage,
    ref(['requested_start', 'number']), // sortBy
    ref([false, false]), // sortDesc
  ),
)
const { jobs: batchJobs, isLoading, error: jobsError, reFetch: reFetchJobs } = useJobsSearch(query)

const { data: batch, isLoading: isBatchLoading, refetch: refetchBatch } = useInvoiceQuery(idRef)

const { data: batchSyncTargets, isInitialLoading: loadingBatchSyncTargets } =
  useInvoiceSyncTargetsQuery(idRef)

const canViewBatch = computed<(batchOwnerId: string | null) => boolean>(
  () => store.getters['permissions/canViewBatch'],
)
const canEditBatch = computed<(batchOwnerId: string | null) => boolean>(
  () => store.getters['permissions/canEditBatch'],
)

const isRateViewer = computed(() => store.getters['permissions/isRateViewer'])

const { canSeeQboPromotion } = usePromotionQuery()
const shouldShowQboPromo = computed(() => {
  return (
    canSeeQboPromotion.value && // has permission to install qbo app
    batchSyncTargets.value?.length === 0 && // no sync targets already installed
    !loadingBatchSyncTargets.value // the above is not because we are still loading
  )
})

// Export CSV
const {
  exportBatchToCsv,
  saving: exportCsvSaving,
  error: exportCsvError,
} = useBatchCsvExport(idRef)

const {
  exportBatchToPdf,
  saving: exportPdfSaving,
  error: exportPdfError,
} = useBatchPdfExport(idRef)

// --------------------------------------Lifecycle hooks------------------------------------------
onMounted(async () => {
  selectedFilters.value = jobStatuses
})
onBeforeRouteUpdate(async (to, from, next) => {
  if (from.params.id === 'new' && !to.params.id) {
    await Promise.all([reFetchJobs(), refetchBatch()])
    selectedJobs.value = batchJobs.value.filter(
      (job) =>
        selectedJobs.value.map((j) => j.number).includes(job.number) &&
        selectedFilters.value.map((f) => f.id).includes(job.status),
    )
  }
  next()
})
onBeforeUnmount(() => {
  selectedJobs.value = []
  showSnackbarError.value = false
})

watch(Object.values(filters), () => {
  updatePage(1)
})
// ----------------------------------End Lifecycle hooks------------------------------------------

// -------------------------------------------Vuex------------------------------------------------
const jobIsSaving = computed<boolean>(() => store.state.jobs.saving)
watch(jobIsSaving, async (isSaving, wasSaving) => {
  if (wasSaving && !isSaving) {
    await Promise.all([reFetchJobs(), refetchBatch()])
    selectedJobs.value = batchJobs.value.filter(
      (job) =>
        selectedJobs.value.map((j) => j.number).includes(job.number) &&
        selectedFilters.value.map((f) => f.id).includes(job.status),
    )
  }
})

const selectedJobs = ref<JobSearchResponse[]>([])

// --------------------------------------Computed properties--------------------------------------
const filterOptions = computed(() =>
  jobStatuses.map((s) => ({
    ...s,
    count: jobStatusCount.value[s.id] || 0,
  })),
)

const formattedBatchDate = computed(() =>
  batch.value ? moment.utc(batch.value.invoiceDate).local().format('MMM DD YYYY') : '',
)

const formattedDueDate = computed(() =>
  batch.value && batch.value.dueDate
    ? moment.utc(batch.value.dueDate).local().format('MMM DD YYYY')
    : '',
)

const formattedBatchTotalAmount = computed(() => formatter.format(batch.value?.totalAmount ?? 0))

const jobStatusCount = computed(() => ({
  [REQUESTED]: batch.value?.requestedJobCount ?? 0,
  [ASSIGNED_TO_SERVICE_PROVIDER]: batch.value?.assignedToServiceProviderJobCount ?? 0,
  [SCHEDULED]: batch.value?.scheduledJobCount ?? 0,
  [ACTIVE]: batch.value?.activeJobCount ?? 0,
  [PAUSED]: batch.value?.pausedJobCount ?? 0,
  [COMPLETED]: batch.value?.completedJobCount ?? 0,
  [CANCELLED]: batch.value?.cancelledJobCount ?? 0,
}))

const jobCountByStatus = computed(() => {
  const jobCountByStatus = {
    completed: batch.value?.completedJobCount,
    active: (batch.value?.activeJobCount ?? 0) + (batch.value?.pausedJobCount ?? 0),
    unstarted:
      (batch.value?.requestedJobCount ?? 0) +
      (batch.value?.scheduledJobCount ?? 0) +
      (batch.value?.assignedToServiceProviderJobCount ?? 0),
  }

  return orderedStatuses.map((status) => ({
    count: jobCountByStatus[status] || 0,
    name: status,
  }))
})

const isJobFormVisible = computed(() => route.name?.includes('ticket-info-panel') ?? false)

function handleQbdSyncRequested(syncTargetId: string) {
  isQbdBottomSheetOpen.value = true
  qbdSyncTargetId.value = syncTargetId
}

const updateJobsLabel = computed(() => {
  if (selectedJobs.value.length === 1) return 'Update job'
  return `Update ${selectedJobs.value.length} jobs`
})

const showCheckbox = computed(() => selectedJobs.value.length > 0)

const areAllJobsSelected = computed(() => {
  const selectedJobIds = selectedJobs.value.map((j) => j.number)
  for (const job of batchJobs.value) {
    if (!selectedJobIds.includes(job.number)) {
      return false
    }
  }
  return true
})

const selectAllButtonLabel = computed(() =>
  areAllJobsSelected.value ? 'Deselect all' : 'Select all',
)
// ----------------------------------End Computed properties--------------------------------------

// ----------------------------------------Methods------------------------------------------------
function dismiss() {
  router.push({
    name: props.parentRouteName,
    params: props.parentRouteParams,
  })
}

function clearSnackbarMessage() {
  snackbarMessage.value = null
}

function clearSnackbarError() {
  showSnackbarError.value = false
}

function isJobSelected(job: JobSearchResponse) {
  return selectedJobs.value.map((job) => job.number).includes(job.number)
}

function handleSelectDeselectAll() {
  if (areAllJobsSelected.value) {
    selectedJobs.value = []
  } else {
    batchJobs.value.forEach((job) => {
      if (!selectedJobs.value.map((j) => j.number).includes(job.number)) {
        selectedJobs.value.push(job)
      }
    })
  }
}

async function handleBulkUpdate(updatedJobs: JobSearchResponse[]) {
  await Promise.all([reFetchJobs(), refetchBatch()])
  selectedJobs.value = updatedJobs.filter(
    (job) =>
      selectedFilters.value.map((f) => f.id).includes(job.status) && job.invoice_id === idRef.value,
  )
  if (selectedJobs.value.length === 0) {
    isBulkUpdateFormOpen.value = false
  }
}

async function handleJobSaved(newJob: { jobNumber: number | null }) {
  await Promise.all([reFetchJobs(), refetchBatch()])
  selectedJobs.value = batchJobs.value.filter(
    (job) =>
      selectedJobs.value.map((j) => j.number).includes(job.number) &&
      job.number !== newJob.jobNumber &&
      selectedFilters.value.map((f) => f.id).includes(job.status),
  )
}

function toggleShowEditForm() {
  showEditForm.value = !showEditForm.value
}

function showHistoryForTarget(targetId: string) {
  showHistoryForTargetId.value = targetId
}

function closeHistoryPanel() {
  showHistoryForTargetId.value = null
}

// -------------------------------------End Methods------------------------------------------------
</script>

<style scoped>
.description {
  margin-top: calc(-1 * var(--space-base));
  margin-bottom: var(--space-base);
}

.btn {
  height: 48px;
  width: 48px;
}

.btn-text {
  text-align: center;
  color: var(--color-blue);
  font-weight: bold;
}
</style>
